import { use, useCallback, useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import styled from 'styled-components';
import { Copyright } from '../../../common/src/model.ts';
import SkipBackIcon from '../assets/play-skip-back.svg?react';
import SkipFowardIcon from '../assets/play-skip-forward.svg?react';
import { LocalStorageKey } from '../data/client_local_storage.ts';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { SongOptionsSetting } from '../pages/settings/song_options.tsx';
import { isInsideAppleAppStoreApp, isMobile } from '../util/billing.ts';
import { generateHymnUrlFromSlug, useCurrentPage } from '../util/path.ts';
import { PdfViewer } from '../util/pdf_viewer.tsx';
import { OuterFrame } from '../util/shared.tsx';
import { Mixer, MixerBoundingBox, MixerButton, playButtonMargin } from './mixer.tsx';
import { mixerLandscapeContainerQuery, mixerPortraitContainerQuery, playerContainerName } from './mixer_controls.tsx';
import { PlayButton } from './play_button.tsx';
import { FadingButton, PlayerContext, swipeDeltas, swipeDurationMs, useButtonFade } from './player_util.tsx';
import { ReportSongIssue } from './report_song_issue.tsx';
import { SequencerContext } from './sequencer.tsx';
import { SlideMenu } from './slide_menus.tsx';
import { useKeepScreenAwake } from './use_keep_screen_awake.ts';

export function SlidingPlayerContainer() {
  const [isPlayerMaximized] = useLocalStorage(LocalStorageKey.IsPlayerMaximized);
  return (
    <SlidingPlayerContainerWrapper $isPlayerMaximized={isPlayerMaximized}>
      <Player />
    </SlidingPlayerContainerWrapper>
  );
}

export function Player({ initiallyExpandedKebabSetting }: { initiallyExpandedKebabSetting?: SongOptionsSetting } = {}) {
  const [isPlayerMaximized] = useLocalStorage(LocalStorageKey.IsPlayerMaximized);
  const { copyrightIssues, hymn, nextSong, previousSong, hasVocals, hasMidi } = use(SequencerContext);
  const hasAudio = hasVocals || hasMidi;
  const [isPdfScrolledToTop, setIsPdfScrolledToTop] = useState(true);
  const [isMixerVisible, setIsMixerVisible] = useLocalStorage(LocalStorageKey.IsMixerVisible);
  const {
    onMinimize,
    isReportSongIssueVisible,
    onShowReportSongIssue,
    onHideReportSongIssue,
    areSongSettingsVisible,
    onShowSongSettingsMenu,
    onHideSongSettingsMenu,
  } = use(PlayerContext);
  const [pdfAutoDisplay] = useLocalStorage(LocalStorageKey.PdfAutoDisplay);
  const page = useCurrentPage();
  useKeepScreenAwake();

  useEffect(() => {
    if (!hymn || !page || !isPlayerMaximized) {
      return;
    }
    const hymnUrl = generateHymnUrlFromSlug({ songSlug: hymn.slug, page });
    history.replaceState(history.state, document.title, hymnUrl);
  }, [hymn, isPlayerMaximized, page]);

  const onSwipedDown = useCallback(() => {
    if (isPdfScrolledToTop) {
      onMinimize();
    }
  }, [isPdfScrolledToTop, onMinimize]);

  const swipeHandler = useSwipeable({
    delta: swipeDeltas,
    swipeDuration: swipeDurationMs,
    // confine left/right swipe to button box for now to avoid sliders, until we switch swipe to be from far left
    onSwipedLeft: isMixerVisible ? undefined : nextSong,
    onSwipedRight: isMixerVisible ? undefined : previousSong,
    onSwipedDown: onSwipedDown,
  });

  const onClickPlay = useCallback(
    (playing: boolean) => {
      if (pdfAutoDisplay && !playing) {
        setIsMixerVisible(false);
      }
    },
    [pdfAutoDisplay, setIsMixerVisible],
  );

  const onClickMixerBackground = useCallback(() => {
    if (areSongSettingsVisible) {
      return;
    }
    setIsMixerVisible(false);
  }, [areSongSettingsVisible, setIsMixerVisible]);

  return (
    <PlayerOuterFrame {...swipeHandler} onClick={onClickMixerBackground}>
      <PdfViewer
        onMinimize={onMinimize}
        onKebabClick={onShowSongSettingsMenu}
        setIsPdfScrolledToTop={setIsPdfScrolledToTop}
      />
      <SlideMenu
        show={areSongSettingsVisible}
        showModal={onShowReportSongIssue}
        close={onHideSongSettingsMenu}
        hymn={hymn}
        initiallyExpandedKebabSetting={initiallyExpandedKebabSetting}
      />
      <ReportSongIssue close={onHideReportSongIssue} visible={isReportSongIssueVisible} hymn={hymn} />
      {!copyrightIssues?.includes(Copyright.Music) && hasAudio && (
        <MixerBoundingBox $isMobile={isMobile()} $isMixerVisible={isMixerVisible}>
          <Mixer />
          <PlayerButtonBox>
            <NextPrevSongButton type={NextPrevButtonType.Previous} />
            <PlayButtonWrapper>
              <PlayButton onClick={onClickPlay} abovePdf={true} />
            </PlayButtonWrapper>
            <NextPrevSongButton type={NextPrevButtonType.Next} />
            <MixerButton />
          </PlayerButtonBox>
        </MixerBoundingBox>
      )}
    </PlayerOuterFrame>
  );
}

// Establishes the named size container that Mixer/MixerControls/Player layout queries
// against. Layout is driven by the player's own dimensions rather than the viewport,
// so Storybook stories rendering the player at a fixed size get the correct portrait
// or landscape layout. `contain: layout` also makes the frame the containing block for
// fixed-positioned descendants (e.g. the song-settings slide-out), so they are scoped to
// the player rather than the viewport — production unaffected because the player already
// fills the viewport. `overflow: hidden` clips the kebab/slide menus when their
// hidden-state position pushes them just below the player.
const PlayerOuterFrame = styled(OuterFrame)`
  container-type: size;
  container-name: ${playerContainerName};
  contain: layout size;
  overflow: hidden;
`;

const SlidingPlayerContainerWrapper = styled.div<{ $isPlayerMaximized: boolean }>`
  z-index: 10; /* to get above add song button and liturgy planner components */
  --player-height: ${isInsideAppleAppStoreApp() ? '100vh' : 'var(--view-height, 100vh)'};
  top: 0;
  transform: translateY(${(props) => (props.$isPlayerMaximized ? '0' : 'var(--player-height)')});
  position: absolute;
  width: 100%;
  height: var(--player-height);
  transition: transform 250ms ease-out;
`;

const PlayerButtonBox = styled.div`
  position: absolute;
  @container ${mixerPortraitContainerQuery} {
    width: 100%;
    height: var(--play-button-size);
    left: 0;
    bottom: ${playButtonMargin};
  }
  @container ${mixerLandscapeContainerQuery} {
    width: var(--play-button-size);
    height: 100%;
    top: 0;
    right: ${playButtonMargin};
  }
  background-color: transparent;
  pointer-events: none;
`;

const PlayButtonWrapper = styled.div`
  position: absolute;
  @container ${mixerPortraitContainerQuery} {
    /* Center horizontally */
    left: 50%;
    transform: translateX(-50%);
  }
  @container ${mixerLandscapeContainerQuery} {
    /* Center vertically */
    top: 50%;
    transform: translateY(-50%);
  }
  width: var(--play-button-size);
  height: var(--play-button-size);
  fill: var(--color-text-black);
  pointer-events: all;
`;

enum NextPrevButtonType {
  Previous = 'previous',
  Next = 'next',
}

function NextPrevSongButton({ type }: { type: NextPrevButtonType }) {
  const fade = useButtonFade();
  const { nextSong, previousSong } = use(SequencerContext);

  const onClick = useCallback(
    (event: any) => {
      if (type === NextPrevButtonType.Previous) {
        previousSong();
      } else {
        nextSong();
      }
      event.stopPropagation(); // prevent mixer from closing
    },
    [nextSong, previousSong, type],
  );

  return (
    <NextPrevButtonWrapper
      $isLeft={type === NextPrevButtonType.Previous}
      $fade={fade}
      $noShadow={true}
      onClick={onClick}
    >
      {type === NextPrevButtonType.Previous ? <SkipBackIcon /> : <SkipFowardIcon />}
    </NextPrevButtonWrapper>
  );
}

const NextPrevButtonWrapper = styled(FadingButton)<{ $isLeft: boolean }>`
  /* these buttons sit over the sheet music, so follow the pdf theme rather than the app theme:
     in app dark mode with a light pdf, --color-text is white and the buttons vanished */
  fill: var(--color-pdf-text);
  @container ${mixerLandscapeContainerQuery} {
    display: none; // hide next/prev buttons in landscape
  }
  @container ${mixerPortraitContainerQuery} {
    position: absolute;
    width: calc(var(--play-button-size) * 0.7);
    border-radius: 50%;
    padding: calc(var(--play-button-size) * 0.16);

    /* Center vertically & horizontally */
    top: 50%;
    transform: translateY(-50%) translateX(-50%);

    left: ${(props) => (props.$isLeft ? '27%' : '73%')};
    cursor: pointer;
    pointer-events: all;
  }
`;
